import {
	ExportFormat,
	exportUnpaidCompletedWorkReport,
	type FsmCompletedWorkPayoutStatus,
	type FsmConnectionDto,
	getUnpaidCompletedWorkReport,
	type UnpaidCompletedWorkReportDto,
	type UnpaidCompletedWorkReportLineDto,
	UnpaidCompletedWorkReportDateBasis,
} from "@fp/admin-api-client";
import { useListFsmConnections } from "@fp/admin-api-client/fsm-connections/fsm-connections.client";
import { DateRangePicker } from "@fp/ui/components/application/date-picker/date-range-picker";
import { ButtonGroup, ButtonGroupItem } from "@fp/ui/components/base/button-group/button-group";
import { PageContentContainer, PageLoading } from "@fp/ui/components/base/page-layout/page-layout";
import { NativeSelect } from "@fp/ui/components/base/select/select-native";
import { formatLocalDate } from "@fp/ui/components/common/local-date-time";
import { cx } from "@fp/ui/utils/cx";
import { endOfMonth, getLocalTimeZone, startOfMonth, today, type DateValue } from "@internationalized/date";
import type { Key, RangeValue } from "@react-types/shared";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useCallback, useState } from "react";
import { AdminLayout } from "@/components/admin-layout";
import { AdminPageHeader } from "@/components/admin-page-header";
import { ReportExportButton } from "@/components/report-export-button";
import { getErrorMessage } from "@/lib/axios";
import { fsmCompletedWorkTypeLabels, fsmPlatformLabels } from "@/lib/fsm-platform";
import { formatCurrency } from "@/lib/payout-utils";
import { unpaidCompletedWorkReportExportClient } from "@/lib/report-export-clients";
import { toast } from "@/lib/toast";
import { getUnpaidCompletedWorkStatusLabel, unpaidCompletedWorkStatusOptions } from "@/lib/unpaid-completed-work-status";

export const Route = createFileRoute("/reporting/unpaid-completed-work/")({
	component: RouteComponent,
	pendingComponent: () => <PageLoading />,
});

const parent = { url: "/reporting", label: "Reporting" };

const now = today(getLocalTimeZone());

const defaultRange: RangeValue<DateValue> = { start: startOfMonth(now), end: endOfMonth(now) };

const dateBasisLabels: Record<UnpaidCompletedWorkReportDateBasis, string> = {
	[UnpaidCompletedWorkReportDateBasis.CompletionDate]: "Completion date",
	[UnpaidCompletedWorkReportDateBasis.RequestCreatedDate]: "Request date",
};

const toIsoDate = (date: DateValue) => date.toDate(getLocalTimeZone()).toISOString();

const firstSelected = <T extends string>(keys: Iterable<Key>, fallback: T): T => {
	for (const key of keys) {
		return key as T;
	}

	return fallback;
};

type ReportFilters = {
	connectionId: string;
	dateBasis: UnpaidCompletedWorkReportDateBasis;
	statuses: Set<FsmCompletedWorkPayoutStatus>;
};

const toReportParams = (range: RangeValue<DateValue>, filters: ReportFilters) => ({
	fsmConnectionId: filters.connectionId,
	dateBasis: filters.dateBasis,
	startDate: toIsoDate(range.start),
	endDate: toIsoDate(range.end),
	statuses: [...filters.statuses],
});

function RouteComponent() {
	const { data: connectionsResponse, isLoading: isLoadingConnections } = useListFsmConnections();
	const connections: FsmConnectionDto[] = connectionsResponse?.data ?? [];

	if (isLoadingConnections) {
		return <PageLoading />;
	}

	return (
		<AdminLayout isWide>
			<AdminPageHeader title="Unpaid Completed Work" parent={parent} />
			<PageContentContainer className="space-y-6">
				{connections.length === 0 ? (
					<p className="text-sm text-gray-700">
						This report covers completed work synced from your field service software.{" "}
						<Link to="/integrations" className="text-brand-secondary underline">
							Connect it on the Integrations page
						</Link>{" "}
						first.
					</p>
				) : (
					<ReportPage connections={connections} />
				)}
			</PageContentContainer>
		</AdminLayout>
	);
}

function ReportPage({ connections }: { connections: FsmConnectionDto[] }) {
	const [dateRange, setDateRange] = useState<RangeValue<DateValue>>(defaultRange);
	const [filters, setFilters] = useState<ReportFilters>({ connectionId: connections[0].id, dateBasis: UnpaidCompletedWorkReportDateBasis.CompletionDate, statuses: new Set() });
	const [report, setReport] = useState<UnpaidCompletedWorkReportDto | null>(null);
	const [isLoading, setIsLoading] = useState(false);

	const fetchReport = useCallback(async (range: RangeValue<DateValue>, nextFilters: ReportFilters) => {
		setIsLoading(true);
		try {
			const response = await getUnpaidCompletedWorkReport(toReportParams(range, nextFilters));
			setReport(response.data);
		} catch (err) {
			toast.error(getErrorMessage(err, "Failed to load the report"));
		} finally {
			setIsLoading(false);
		}
	}, []);

	const applyFilters = useCallback(
		(nextFilters: ReportFilters) => {
			setFilters(nextFilters);
			fetchReport(dateRange, nextFilters);
		},
		[dateRange, fetchReport],
	);

	const handleApply = useCallback(() => applyFilters(filters), [filters, applyFilters]);

	const exportReport = useCallback((format: ExportFormat) => exportUnpaidCompletedWorkReport({ ...toReportParams(dateRange, filters), format }), [dateRange, filters]);

	const exportKey = JSON.stringify(toReportParams(dateRange, filters));

	return (
		<>
			<div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
				<div className="flex flex-col gap-4 sm:flex-row sm:items-center">
					{connections.length > 1 && (
						<div className="w-full sm:w-56">
							<NativeSelect
								aria-label="Field service software"
								value={filters.connectionId}
								onChange={(event) => applyFilters({ ...filters, connectionId: event.target.value })}
								options={connections.map((connection) => ({ value: connection.id, label: fsmPlatformLabels[connection.platform] }))}
							/>
						</div>
					)}
					<ButtonGroup
						size="sm"
						aria-label="Date the range applies to"
						disallowEmptySelection
						selectedKeys={[filters.dateBasis]}
						onSelectionChange={(keys) => applyFilters({ ...filters, dateBasis: firstSelected(keys, filters.dateBasis) })}
					>
						{Object.values(UnpaidCompletedWorkReportDateBasis).map((dateBasis) => (
							<ButtonGroupItem key={dateBasis} id={dateBasis}>
								{dateBasisLabels[dateBasis]}
							</ButtonGroupItem>
						))}
					</ButtonGroup>
					<DateRangePicker defaultOpen value={dateRange} onChange={(range) => range && setDateRange(range)} onApply={handleApply} />
				</div>
				{report && (
					<div className="flex gap-2">
						<ReportExportButton key={`csv-${exportKey}`} client={unpaidCompletedWorkReportExportClient} format={ExportFormat.Csv} label="CSV" request={exportReport} />
						<ReportExportButton key={`pdf-${exportKey}`} client={unpaidCompletedWorkReportExportClient} format={ExportFormat.Pdf} label="PDF" request={exportReport} />
					</div>
				)}
			</div>

			{report && <StatusFilter selected={filters.statuses} onSelectedChange={(statuses) => applyFilters({ ...filters, statuses })} />}

			{isLoading && (
				<div className="flex items-center justify-center py-12">
					<div className="text-gray-500">Loading report...</div>
				</div>
			)}

			{!isLoading && !report && <div className="py-12 text-center text-gray-500">Select a date range and click Apply to generate the report.</div>}

			{!isLoading && report && <UnpaidCompletedWorkReportView report={report} isFiltered={filters.statuses.size > 0} />}
		</>
	);
}

function UnpaidCompletedWorkReportView({ report, isFiltered }: { report: UnpaidCompletedWorkReportDto; isFiltered: boolean }) {
	if (report.lines.length === 0) {
		return <div className="py-12 text-center text-gray-500">{isFiltered ? "No unpaid completed work matches the current filters." : "No unpaid completed work found for the selected period."}</div>;
	}

	return (
		<div className="space-y-3">
			<p className="text-sm text-gray-500">
				{report.lines.length} {report.lines.length === 1 ? "job" : "jobs"} as of {report.lastSyncedAt ? `the ${fsmPlatformLabels[report.platform]} sync on ${formatLocalDate(report.lastSyncedAt, "datetimeWithYear")}` : "now"}. Paid work is left off.
			</p>
			<div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
				<table className="w-full text-sm">
					<thead>
						<tr className="border-b border-gray-200 text-left text-xs font-medium text-gray-500">
							<th className="px-4 py-2">Payee</th>
							<th className="px-4 py-2">Completed</th>
							<th className="px-4 py-2">Requested</th>
							<th className="px-4 py-2">Customer</th>
							<th className="px-4 py-2">Address</th>
							<th className="px-4 py-2">Description</th>
							<th className="px-4 py-2">Job Ref</th>
							<th className="px-4 py-2 text-right">Amount</th>
							<th className="px-4 py-2">Status</th>
							<th className="px-4 py-2">Next Step</th>
						</tr>
					</thead>
					<tbody>
						{report.lines.map((line) => (
							<UnpaidCompletedWorkReportRow key={`${line.externalType}:${line.externalId}:${line.techEmail ?? ""}`} line={line} />
						))}
					</tbody>
				</table>
			</div>
		</div>
	);
}

function UnpaidCompletedWorkReportRow({ line }: { line: UnpaidCompletedWorkReportLineDto }) {
	const payeeLabel = line.payeeName ?? line.techName ?? line.techEmail ?? "";

	return (
		<tr className="border-b border-gray-100 align-top last:border-b-0">
			<td className="px-4 py-2 text-gray-900">
				<div>{payeeLabel}</div>
				{line.techEmail && line.techEmail !== payeeLabel && <div className="text-xs text-gray-500">{line.techEmail}</div>}
			</td>
			<td className="px-4 py-2 whitespace-nowrap text-gray-900">{formatLocalDate(line.completedAt, "dateWithYear")}</td>
			<td className="px-4 py-2 whitespace-nowrap text-gray-900">{line.requestCreatedDate && formatLocalDate(line.requestCreatedDate, "dateWithYear")}</td>
			<td className="px-4 py-2 text-gray-900">{line.customerName}</td>
			<td className="px-4 py-2 text-gray-900">{line.address}</td>
			<td className="px-4 py-2 text-gray-900">{line.description}</td>
			<td className="px-4 py-2 text-gray-500">
				<div className="whitespace-nowrap">{fsmCompletedWorkTypeLabels[line.externalType]}</div>
				<div className="break-all text-xs">{line.externalId}</div>
			</td>
			<td className="px-4 py-2 text-right whitespace-nowrap text-gray-900">{line.amount !== null && line.amount !== undefined && formatCurrency(line.amount, "USD")}</td>
			<td className="min-w-56 px-4 py-2 text-gray-900">
				<div className="font-medium">{getUnpaidCompletedWorkStatusLabel(line.status)}</div>
				<div className="text-xs text-gray-700">{line.explanation}</div>
			</td>
			<td className="px-4 py-2 text-xs text-gray-700">{line.nextStep}</td>
		</tr>
	);
}

function StatusFilter({ selected, onSelectedChange }: { selected: Set<FsmCompletedWorkPayoutStatus>; onSelectedChange: (value: Set<FsmCompletedWorkPayoutStatus>) => void }) {
	const toggle = (status: FsmCompletedWorkPayoutStatus) => {
		const next = new Set(selected);

		if (next.has(status)) {
			next.delete(status);
		} else {
			next.add(status);
		}

		onSelectedChange(next);
	};

	return (
		<div>
			<h3 className="mb-2 text-sm font-semibold text-gray-900">Status</h3>
			<p className="mb-2 text-xs text-gray-500">Leave every status unselected to include all unpaid work.</p>
			<div className="flex flex-wrap gap-2">
				{unpaidCompletedWorkStatusOptions.map((option) => (
					<button
						key={option.id}
						type="button"
						onClick={() => toggle(option.id)}
						aria-pressed={selected.has(option.id)}
						className={cx(
							"rounded-md px-3 py-1.5 text-sm font-medium transition",
							selected.has(option.id) ? "bg-brand-secondary text-brand-400 hover:bg-brand-secondary_hover" : "bg-white text-gray-700 ring-1 ring-inset ring-black/10 hover:bg-gray-50",
						)}
					>
						{option.label}
					</button>
				))}
			</div>
		</div>
	);
}
